import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, errorMessage } from '../../shared/api/client';
import { Alert, Button, Field, PasswordField } from '../../shared/ui/components';
import { fieldErrorsFrom, showFieldErrors, type FieldError } from '../../shared/ui/fieldErrors';
import { useSession } from '../session/SessionProvider';
import { AuthLayout } from './AuthLayout';
import { NAME_PATTERN, tidyName } from '../../shared/geo/places';
import { joinPath } from '../churches/JoinByInvitePage';
import { PRIVACY_VERSION } from '../legal/PrivacyPage';
import { ChangeEmail } from './ChangeEmail';
import { GoogleButton } from './GoogleButton';
import { useAsync } from '../../shared/ui/useAsync';
import { safeReturnPath } from './LoginPage';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Crea una cuenta (una Persona global en KEORA) y entra. */
/** Adónde ir después de confirmar el correo (el enlace llega por correo, sin sesión). */
export const AFTER_CONFIRM_KEY = 'keora.afterConfirm';

/** Crea una cuenta (una Persona global en KEORA). Para entrar, primero se confirma el correo. */
export function RegisterPage() {
  const { state } = useSession();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [similar, setSimilar] = useState<{ accounts: SimilarAccount[]; payload: Record<string, unknown> } | null>(null);
  const joinId = params.get('unirse');
  const joining = joinId && UUID.test(joinId) ? joinId : null;
  const next = joining ? joinPath(joining, true) : params.get('siguiente') === 'iglesia' ? '/app/iglesias/nueva' : '/app';

  if (params.get('google') === '1') return <GoogleSignup />;
  if (state.status === 'authenticated') return <Navigate to={next} replace />;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const email = String(form.get('email') ?? '').trim();
    const password = String(form.get('password') ?? '');
    const { details, fields } = readBasicName(form);
    const errors: FieldError[] = [...fields];
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errors.push({ name: 'email', message: 'Escribe tu correo completo, por ejemplo nombre@gmail.com.' });
    if (password.length < 10) errors.push({ name: 'password', message: 'La contraseña necesita al menos 10 caracteres.' });
    if (!form.get('privacy')) errors.push({ name: 'privacy', message: 'Para crear tu cuenta necesitas aceptar el Aviso de Privacidad.' });
    if (errors.length) {
      setError(errors.length === 1 ? errors[0]!.message : `Revisa los ${errors.length} campos marcados en rojo.`);
      showFieldErrors(formEl, errors);
      return;
    }
    await create({ ...details, email, password, privacyVersion: PRIVACY_VERSION }, formEl);
  }

  async function create(payload: Record<string, unknown>, formEl: HTMLFormElement | null) {
    setBusy(true);
    setError(null);
    try {
      await api.post('/identity/accounts', payload);
      try {
        localStorage.setItem(AFTER_CONFIRM_KEY, next);
      } catch {
        // Sin almacenamiento: después de confirmar se va al inicio.
      }
      setSimilar(null);
      setSentTo(String(payload.email));
    } catch (e) {
      if (e instanceof ApiError && e.details?.reason === 'possible_duplicate') {
        setSimilar({ accounts: (e.details.accounts as SimilarAccount[]) ?? [], payload });
        return;
      }
      if (!formEl) {
        setSimilar(null);
        setError(errorMessage(e));
        return;
      }
      const fieldErrors = fieldErrorsFrom(e);
      setError(errorMessage(e));
      if (fieldErrors.length) showFieldErrors(formEl, fieldErrors);
      else if (errorMessage(e).includes('correo')) showFieldErrors(formEl, [{ name: 'email', message: errorMessage(e) }]);
    } finally {
      setBusy(false);
    }
  }

  if (sentTo) return <CheckYourEmail email={sentTo} />;
  if (similar) {
    return (
      <SimilarAccounts
        accounts={similar.accounts}
        busy={busy}
        onNotMe={() => void create({ ...similar.payload, notDuplicate: true }, null)}
        onBack={() => setSimilar(null)}
      />
    );
  }

  return (
    <AuthLayout
      title="Crear cuenta"
      footer={
        <>
          ¿Ya tienes cuenta? <Link to={joining ? `/entrar?volver=${encodeURIComponent(joinPath(joining, true))}` : '/entrar'}>Iniciar sesión</Link>
        </>
      }
    >
      <p className="muted">
        {joining ? 'Al crear tu cuenta, tu solicitud se enviará a la iglesia que te invitó.' : 'Una sola cuenta te sirve en cualquier iglesia que use KEORA.'}
      </p>
      <GoogleButton label="Crear cuenta con Google" volver={next} />
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <BasicNameFields />
        <Field label="Correo" name="email" type="email" autoComplete="email" required hint="Te enviaremos un enlace para confirmarlo." />
        <PasswordField label="Contraseña" name="password" autoComplete="new-password" required minLength={10} hint="Mínimo 10 caracteres. Toca el ojito para ver lo que escribes." />
        <PrivacyConsent />
        <Button type="submit" busy={busy} className="btn--block">
          Crear cuenta
        </Button>
      </form>
    </AuthLayout>
  );
}

/**
 * Al crear la cuenta solo se pide nombre y apellido; género, nacimiento y lo demás se completan
 * después en Mi perfil (Inicio lo recuerda con «Completa tus datos»).
 */
function BasicNameFields({ givenName, surname }: { givenName?: string; surname?: string }) {
  const tidy = (e: { currentTarget: HTMLInputElement }) => {
    e.currentTarget.value = tidyName(e.currentTarget.value);
  };
  return (
    <div className="form__row">
      <Field label="Nombre(s)" name="givenName" required maxLength={100} autoComplete="given-name" defaultValue={givenName} onBlur={tidy} />
      <Field label="Apellidos" name="surname" required maxLength={100} autoComplete="family-name" defaultValue={surname} onBlur={tidy} />
    </div>
  );
}

/** Nombre y apellidos del registro tal como los guarda la API (los apellidos completos van como paterno). */
function readBasicName(form: FormData): { details: { givenName: string; paternalSurname: string; maternalSurname: string }; fields: FieldError[] } {
  const text = (name: string) => tidyName(String(form.get(name) ?? '').trim().replace(/\s+/g, ' '));
  const details = { givenName: text('givenName'), paternalSurname: text('surname'), maternalSurname: '' };
  const fields: FieldError[] = [];
  if (!details.givenName) fields.push({ name: 'givenName', message: 'Escribe tu nombre.' });
  else if (!NAME_PATTERN.test(details.givenName)) fields.push({ name: 'givenName', message: 'El nombre solo lleva letras (sin números ni símbolos).' });
  if (!details.paternalSurname) fields.push({ name: 'surname', message: 'Escribe tu apellido.' });
  else if (!NAME_PATTERN.test(details.paternalSurname)) fields.push({ name: 'surname', message: 'El apellido solo lleva letras (sin números ni símbolos).' });
  return { details, fields };
}

/** El consentimiento del Aviso de Privacidad (igual con contraseña o con Google). */
function PrivacyConsent() {
  return (
    <label className="consent">
      <input type="checkbox" name="privacy" required />
      <span>
        He leído el{' '}
        <Link to="/privacidad" target="_blank">
          Aviso de Privacidad
        </Link>{' '}
        y doy mi consentimiento expreso para el tratamiento de mis datos personales, incluidos los sensibles (como mi pertenencia a una iglesia y mis
        creencias religiosas).
      </span>
    </label>
  );
}

/**
 * Entró con Google sin tener cuenta: el correo ya viene confirmado por Google; solo faltan sus datos
 * y aceptar el Aviso de Privacidad. Al terminar entra directo, sin enlace por correo.
 */
function GoogleSignup() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const signup = useAsync((signal) => api.get<{ email: string; givenName: string; familyName: string }>('/identity/google/signup', signal), []);

  if (signup.status === 'loading') return <AuthLayout title="Crear cuenta con Google"><p className="muted">Cargando…</p></AuthLayout>;
  if (signup.status === 'error') {
    return (
      <AuthLayout title="Crear cuenta con Google">
        <Alert>Se venció el tiempo para terminar el registro. Vuelve a elegir «Entrar con Google».</Alert>
        <Link className="btn btn--primary btn--block" to="/entrar">
          Volver a Entrar
        </Link>
      </AuthLayout>
    );
  }
  const g = signup.data;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const { details, fields } = readBasicName(form);
    const errors: FieldError[] = [...fields];
    if (!form.get('privacy')) errors.push({ name: 'privacy', message: 'Para crear tu cuenta necesitas aceptar el Aviso de Privacidad.' });
    if (errors.length) {
      setError(errors.length === 1 ? errors[0]!.message : `Revisa los ${errors.length} campos marcados en rojo.`);
      showFieldErrors(formEl, errors);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { volver } = await api.post<{ volver: string }>('/identity/google/signup', { ...details, privacyVersion: PRIVACY_VERSION });
      await refresh();
      navigate(safeReturnPath(volver), { replace: true });
    } catch (e) {
      const fieldErrors = fieldErrorsFrom(e);
      setError(errorMessage(e));
      if (fieldErrors.length) showFieldErrors(formEl, fieldErrors);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Crear cuenta con Google">
      <p className="muted">
        Entrarás con <strong>{g.email}</strong>. Revisa tus datos para terminar.
      </p>
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <BasicNameFields givenName={g.givenName} surname={g.familyName} />
        <PrivacyConsent />
        <Button type="submit" busy={busy} className="btn--block">
          Crear cuenta
        </Button>
      </form>
    </AuthLayout>
  );
}

interface SimilarAccount {
  personId: string;
  email: string;
  verified: boolean;
}

/**
 * Antes de crear otra cuenta con el mismo nombre: casi siempre es alguien que no confirmó su correo
 * o no recuerda su contraseña. Se le ayuda a recuperar la suya; si no es él, sigue con su registro.
 */
function SimilarAccounts({ accounts, busy, onNotMe, onBack }: { accounts: SimilarAccount[]; busy: boolean; onNotMe: () => void; onBack: () => void }) {
  const [sent, setSent] = useState<string | null>(null);
  return (
    <AuthLayout title="¿Ya tienes una cuenta?">
      <p>Encontramos {accounts.length === 1 ? 'una cuenta' : 'cuentas'} con tu mismo nombre. Si es tuya, no necesitas crear otra:</p>
      <ul className="list">
        {accounts.map((a) => (
          <li key={a.personId} className="list__item">
            <div className="stack" style={{ flex: 1 }}>
              <strong>{a.email}</strong>
              {a.verified ? (
                <>
                  <span className="muted small">Correo confirmado. Entra con ese correo; si no recuerdas la contraseña, puedes crear una nueva.</span>
                  <div className="actions actions--wrap">
                    <Link className="btn" to="/entrar">Iniciar sesión</Link>
                    <Link className="btn btn--secondary" to="/olvide-contrasena">Olvidé mi contraseña</Link>
                  </div>
                </>
              ) : (
                <>
                  <span className="muted small">Falta confirmar ese correo. Te mandamos el enlace otra vez para que la puedas usar.</span>
                  {sent === a.personId ? (
                    <Alert tone="success">Listo, revisa ese correo (también en Spam o Promociones) y abre el enlace.</Alert>
                  ) : (
                    <div className="actions actions--wrap">
                      <Button
                        type="button"
                        onClick={() => {
                          setSent(a.personId);
                          void api.post('/identity/email-verifications/resend-similar', { personId: a.personId }).catch(() => undefined);
                        }}
                      >
                        Enviarme el enlace a ese correo
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="muted small">Si ya no usas ese correo, pide a tu iglesia o a KEORA que lo cambien; así conservas tu cuenta.</p>
      <div className="actions actions--wrap">
        <Button type="button" variant="ghost" busy={busy} onClick={onNotMe}>No soy yo, crear mi cuenta</Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onBack}>Volver</Button>
      </div>
    </AuthLayout>
  );
}

/** Después de registrarse: falta confirmar el correo para poder entrar. */
export function CheckYourEmail({ email: initial }: { email: string }) {
  const [email, setEmail] = useState(initial);
  const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
  async function resend() {
    setState('busy');
    await api.post('/identity/email-verifications/resend', { email }).catch(() => undefined);
    setState('sent');
  }
  return (
    <AuthLayout title="Revisa tu correo">
      <Alert tone="success">
        Te enviamos un enlace a <strong>{email}</strong>. Ábrelo para confirmar tu correo y poder entrar.
      </Alert>
      <p className="muted">Si no lo ves en unos minutos, busca en Spam o en Promociones.</p>
      <div className="actions actions--wrap">
        <Button variant="secondary" busy={state === 'busy'} disabled={state === 'sent'} onClick={() => void resend()}>
          {state === 'sent' ? 'Enlace enviado otra vez' : 'Enviarme el enlace otra vez'}
        </Button>
        <Link className="btn btn--ghost" to="/entrar">
          Ya lo confirmé, entrar
        </Link>
      </div>
      <ChangeEmail email={email} onChanged={(next) => { setEmail(next); setState('idle'); }} />
    </AuthLayout>
  );
}
